/**
 * Lista e gestione dei costi usati dal ricalcolo margini per unita' organizzativa.
 *
 * Selettore obbligatorio per unita', tabella con modifica inline, aggiunta
 * rapida e soft delete. Centro di costo + modalita' di pagamento per costo.
 * Pannello pagamenti espandibile per ogni costo (storico, con allegato
 * ricevuta e collegamento a movimento/fattura).
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Button,
  Card,
  CardContent,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { ChevronDown, ChevronRight, Paperclip, Plus, Save, Trash2, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormatters } from '@lib/formatters';
import { useUrlState } from '@lib/useUrlState';

/* --- Utility --- */

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

const authHeadersGet = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/* --- Interfacce --- */

interface Unita {
  id: string;
  nome: string;
}

interface Centro {
  id: string;
  codice: string;
  nome: string;
  attivo?: boolean;
}

interface Metodo {
  id: string;
  codice: string;
  descrizione: string;
}

interface CostoFisso {
  id: string;
  descrizione: string;
  parte_fissa: string;
  parte_variabile: string;
  importo: string;
  periodicita: string;
  importo_mensile: string;
  attivo: boolean;
  centro_costo_id: string | null;
  modalita_pagamento: string | null;
}

interface Pagamento {
  id: string;
  costo_fisso_id: string;
  data_pagamento: string;
  importo: string;
  modalita_pagamento: string | null;
  movimento_cassa_id: string | null;
  fattura_id: string | null;
  riferimento: string | null;
  note: string | null;
}

interface NuovoCosto {
  descrizione: string;
  parte_fissa: string;
  parte_variabile: string;
  periodicita: string;
  centro_costo_id: string;
  modalita_pagamento: string;
}

interface NuovoPagamento {
  data_pagamento: string;
  importo: string;
  modalita_pagamento: string;
  riferimento: string;
  note: string;
}

const PERIODICITA = ['mensile', 'bimestrale', 'trimestrale', 'semestrale', 'annuale'];

const oggi = () => new Date().toISOString().slice(0, 10);

const nuovoPagamentoVuoto = (importo: string): NuovoPagamento => ({
  data_pagamento: oggi(),
  importo,
  modalita_pagamento: '',
  riferimento: '',
  note: '',
});

/* --- Componente --- */

export const CostiFissiList: React.FC = () => {
  const fmt = useFormatters();
  const [unita, setUnita] = useState<Unita[]>([]);
  const [unitaId, setUnitaId] = useUrlState('uo', '');
  const [costi, setCosti] = useState<CostoFisso[]>([]);
  const [centri, setCentri] = useState<Centro[]>([]);
  const [metodi, setMetodi] = useState<Metodo[]>([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<CostoFisso>>({});
  const [adding, setAdding] = useState(false);
  const [nuovoCosto, setNuovoCosto] = useState<NuovoCosto>({
    descrizione: '',
    parte_fissa: '',
    parte_variabile: '',
    periodicita: 'mensile',
    centro_costo_id: '',
    modalita_pagamento: '',
  });

  /* Pagamenti: riga espansa + cache per costo + form nuovo pagamento */
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pagamenti, setPagamenti] = useState<Record<string, Pagamento[]>>({});
  const [nuovoPag, setNuovoPag] = useState<NuovoPagamento>(nuovoPagamentoVuoto(''));
  const [uploadMsg, setUploadMsg] = useState<Record<string, string>>({});

  const NUM_COL = 10;

  /* Carica lookup: unita', centri, metodi */
  useEffect(() => {
    fetch('/api/v1/unita', { headers: authHeadersGet() })
      .then((r) => r.json())
      .then((data: Unita[]) => setUnita(data))
      .catch(() => {});
    fetch('/api/v1/centri-costo/centri?limit=200', { headers: authHeadersGet() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setCentri(Array.isArray(data) ? data : (data.items ?? [])))
      .catch(() => {});
    fetch('/api/v1/classificazioni?sistema=metodo_pagamento', { headers: authHeadersGet() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data: Metodo[]) => setMetodi(data))
      .catch(() => {});
  }, []);

  const centroLabel = (cid: string | null): string => {
    if (!cid) return '-';
    const c = centri.find((x) => x.id === cid);
    return c ? c.codice : '-';
  };

  const centriAttivi = centri.filter((c) => c.attivo !== false);

  /* Carica costi fissi per unita' selezionata */
  const caricaCosti = useCallback(async () => {
    if (!unitaId) return;
    setLoading(true);
    const res = await fetch(
      `/api/v1/prezzi/costi-fissi?unita_organizzativa_id=${unitaId}`,
      { headers: authHeadersGet() },
    );
    if (res.ok) setCosti(await res.json());
    setLoading(false);
  }, [unitaId]);

  useEffect(() => {
    caricaCosti();
  }, [caricaCosti]);

  const totaleMensile = costi.reduce((acc, c) => acc + Number(c.importo_mensile), 0);

  /* Aggiunta rapida */
  const salvaNuovo = async () => {
    if (!nuovoCosto.descrizione || (!nuovoCosto.parte_fissa && !nuovoCosto.parte_variabile)) return;
    const res = await fetch('/api/v1/prezzi/costi-fissi', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        unita_organizzativa_id: unitaId,
        descrizione: nuovoCosto.descrizione,
        parte_fissa: nuovoCosto.parte_fissa || '0',
        parte_variabile: nuovoCosto.parte_variabile || '0',
        periodicita: nuovoCosto.periodicita,
        centro_costo_id: nuovoCosto.centro_costo_id || null,
        modalita_pagamento: nuovoCosto.modalita_pagamento || null,
      }),
    });
    if (res.ok) {
      setAdding(false);
      setNuovoCosto({
        descrizione: '', parte_fissa: '', parte_variabile: '',
        periodicita: 'mensile', centro_costo_id: '', modalita_pagamento: '',
      });
      caricaCosti();
    }
  };

  /* Modifica inline */
  const startEdit = (c: CostoFisso) => {
    setEditId(c.id);
    setEditData({
      descrizione: c.descrizione,
      parte_fissa: c.parte_fissa,
      parte_variabile: c.parte_variabile,
      periodicita: c.periodicita,
      centro_costo_id: c.centro_costo_id,
      modalita_pagamento: c.modalita_pagamento,
      attivo: c.attivo,
    });
  };

  const salvaEdit = async () => {
    if (!editId) return;
    const res = await fetch(`/api/v1/prezzi/costi-fissi/${editId}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(editData),
    });
    if (res.ok) {
      setEditId(null);
      caricaCosti();
    }
  };

  const elimina = async (id: string) => {
    await fetch(`/api/v1/prezzi/costi-fissi/${id}`, {
      method: 'DELETE',
      headers: authHeadersGet(),
    });
    caricaCosti();
  };

  /* Pagamenti */
  const caricaPagamenti = useCallback(async (costoId: string) => {
    const res = await fetch(`/api/v1/prezzi/costi-fissi/${costoId}/pagamenti`, {
      headers: authHeadersGet(),
    });
    if (res.ok) {
      const data: Pagamento[] = await res.json();
      setPagamenti((prev) => ({ ...prev, [costoId]: data }));
    }
  }, []);

  const togglePagamenti = (c: CostoFisso) => {
    if (expandedId === c.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(c.id);
    setNuovoPag(nuovoPagamentoVuoto(c.importo_mensile));
    void caricaPagamenti(c.id);
  };

  const registraPagamento = async (costoId: string) => {
    const res = await fetch(`/api/v1/prezzi/costi-fissi/${costoId}/pagamenti`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        data_pagamento: nuovoPag.data_pagamento,
        importo: Number(nuovoPag.importo) || 0,
        modalita_pagamento: nuovoPag.modalita_pagamento || null,
        riferimento: nuovoPag.riferimento || null,
        note: nuovoPag.note || null,
      }),
    });
    if (res.ok) {
      setNuovoPag(nuovoPagamentoVuoto(nuovoPag.importo));
      void caricaPagamenti(costoId);
    }
  };

  const eliminaPagamento = async (costoId: string, pagId: string) => {
    await fetch(`/api/v1/prezzi/costi-fissi/${costoId}/pagamenti/${pagId}`, {
      method: 'DELETE',
      headers: authHeadersGet(),
    });
    void caricaPagamenti(costoId);
  };

  const allegaRicevuta = async (costoId: string, pagId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(
      `/api/v1/prezzi/costi-fissi/${costoId}/pagamenti/${pagId}/allegato`,
      { method: 'POST', headers: authHeadersGet(), body: fd },
    );
    setUploadMsg((prev) => ({
      ...prev,
      [pagId]: res.ok ? 'Ricevuta caricata' : 'Errore upload',
    }));
  };

  const metodoLabel = (cod: string | null): string => {
    if (!cod) return '-';
    return metodi.find((m) => m.codice === cod)?.descrizione ?? cod;
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-outfit font-semibold text-foreground">Costi per ricalcolo margini</h1><p className="text-sm text-muted-foreground mt-1">Questa pagina alimenta listino, break-even e andamento prezzi. Non e' un modulo di contabilita' generale.</p></div>
        {unitaId && (
          <Button size="sm" onClick={() => setAdding(true)} disabled={adding}>
            <Plus className="w-4 h-4 mr-1" />
            Aggiungi
          </Button>
        )}
      </div>

      {/* Selettore unita' organizzativa */}
      <div>
        <label className="text-sm font-medium text-muted-foreground mb-1 block">
          Unita' organizzativa
        </label>
        <select
          value={unitaId}
          onChange={(e) => setUnitaId(e.target.value)}
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm w-64"
        >
          <option value="">Seleziona unita'...</option>
          {unita.map((u) => (
            <option key={u.id} value={u.id}>{u.nome}</option>
          ))}
        </select>
      </div>

      {!unitaId ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Seleziona un&apos;unita' organizzativa per visualizzare i costi usati nel ricalcolo
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Caricamento...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-medium w-8" />
                  <TableHead className="font-medium">Descrizione</TableHead>
                  <TableHead className="font-medium text-right">Parte fissa</TableHead>
                  <TableHead className="font-medium text-right">Parte variabile</TableHead>
                  <TableHead className="font-medium">Periodicita'</TableHead>
                  <TableHead className="font-medium text-right">Mensile</TableHead>
                  <TableHead className="font-medium text-center">Attivo</TableHead>
                  <TableHead className="font-medium">Centro</TableHead>
                  <TableHead className="font-medium">Pagamento</TableHead>
                  <TableHead className="font-medium w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Riga aggiunta rapida */}
                {adding && (
                  <TableRow>
                    <TableCell />
                    <TableCell>
                      <Input
                        value={nuovoCosto.descrizione}
                        onChange={(e) => setNuovoCosto({ ...nuovoCosto, descrizione: e.target.value })}
                        placeholder="Descrizione"
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number" step="0.01"
                        value={nuovoCosto.parte_fissa}
                        onChange={(e) => setNuovoCosto({ ...nuovoCosto, parte_fissa: e.target.value })}
                        placeholder="0.00" className="h-8 text-right"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number" step="0.01"
                        value={nuovoCosto.parte_variabile}
                        onChange={(e) => setNuovoCosto({ ...nuovoCosto, parte_variabile: e.target.value })}
                        placeholder="0.00" className="h-8 text-right"
                      />
                    </TableCell>
                    <TableCell>
                      <select
                        value={nuovoCosto.periodicita}
                        onChange={(e) => setNuovoCosto({ ...nuovoCosto, periodicita: e.target.value })}
                        className="rounded border border-border bg-card text-foreground px-2 py-1 text-sm"
                      >
                        {PERIODICITA.map((p) => (<option key={p} value={p}>{p}</option>))}
                      </select>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">-</TableCell>
                    <TableCell className="text-center text-muted-foreground">Si</TableCell>
                    <TableCell>
                      <select
                        value={nuovoCosto.centro_costo_id}
                        onChange={(e) => setNuovoCosto({ ...nuovoCosto, centro_costo_id: e.target.value })}
                        className="rounded border border-border bg-card text-foreground px-2 py-1 text-sm"
                      >
                        <option value="">-</option>
                        {centriAttivi.map((c) => (
                          <option key={c.id} value={c.id}>{c.codice}</option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell>
                      <select
                        value={nuovoCosto.modalita_pagamento}
                        onChange={(e) => setNuovoCosto({ ...nuovoCosto, modalita_pagamento: e.target.value })}
                        className="rounded border border-border bg-card text-foreground px-2 py-1 text-sm"
                      >
                        <option value="">-</option>
                        {metodi.map((m) => (<option key={m.id} value={m.codice}>{m.descrizione}</option>))}
                      </select>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={salvaNuovo}>
                          <Save className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {costi.length === 0 && !adding ? (
                  <TableRow>
                    <TableCell colSpan={NUM_COL} className="text-center text-muted-foreground py-8">
                      Nessun costo per ricalcolo registrato
                    </TableCell>
                  </TableRow>
                ) : (
                  costi.map((c) => (
                    <React.Fragment key={c.id}>
                      <TableRow>
                        <TableCell>
                          <button
                            className="text-muted-foreground"
                            onClick={() => togglePagamenti(c)}
                            title="Pagamenti"
                          >
                            {expandedId === c.id
                              ? <ChevronDown className="w-4 h-4" />
                              : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </TableCell>
                        <TableCell>
                          {editId === c.id ? (
                            <Input
                              value={editData.descrizione ?? ''}
                              onChange={(e) => setEditData({ ...editData, descrizione: e.target.value })}
                              className="h-8"
                            />
                          ) : c.descrizione}
                        </TableCell>
                        <TableCell className="text-right">
                          {editId === c.id ? (
                            <Input
                              type="number" step="0.01"
                              value={editData.parte_fissa ?? ''}
                              onChange={(e) => setEditData({ ...editData, parte_fissa: e.target.value })}
                              className="h-8 text-right"
                            />
                          ) : fmt.currency(c.parte_fissa)}
                        </TableCell>
                        <TableCell className="text-right">
                          {editId === c.id ? (
                            <Input
                              type="number" step="0.01"
                              value={editData.parte_variabile ?? ''}
                              onChange={(e) => setEditData({ ...editData, parte_variabile: e.target.value })}
                              className="h-8 text-right"
                            />
                          ) : fmt.currency(c.parte_variabile)}
                        </TableCell>
                        <TableCell>
                          {editId === c.id ? (
                            <select
                              value={editData.periodicita ?? ''}
                              onChange={(e) => setEditData({ ...editData, periodicita: e.target.value })}
                              className="rounded border border-border bg-card text-foreground px-2 py-1 text-sm"
                            >
                              {PERIODICITA.map((p) => (<option key={p} value={p}>{p}</option>))}
                            </select>
                          ) : c.periodicita}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {fmt.currency(c.importo_mensile)}
                        </TableCell>
                        <TableCell className="text-center">
                          {editId === c.id ? (
                            <input
                              type="checkbox"
                              checked={editData.attivo ?? true}
                              onChange={(e) => setEditData({ ...editData, attivo: e.target.checked })}
                            />
                          ) : (
                            <span className={c.attivo
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-muted-foreground'}
                            >
                              {c.attivo ? 'Si' : 'No'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {editId === c.id ? (
                            <select
                              value={editData.centro_costo_id ?? ''}
                              onChange={(e) => setEditData({ ...editData, centro_costo_id: e.target.value })}
                              className="rounded border border-border bg-card text-foreground px-2 py-1 text-sm"
                            >
                              <option value="">-</option>
                              {centriAttivi.map((cc) => (<option key={cc.id} value={cc.id}>{cc.codice}</option>))}
                            </select>
                          ) : centroLabel(c.centro_costo_id)}
                        </TableCell>
                        <TableCell>
                          {editId === c.id ? (
                            <select
                              value={editData.modalita_pagamento ?? ''}
                              onChange={(e) => setEditData({ ...editData, modalita_pagamento: e.target.value })}
                              className="rounded border border-border bg-card text-foreground px-2 py-1 text-sm"
                            >
                              <option value="">-</option>
                              {metodi.map((m) => (<option key={m.id} value={m.codice}>{m.descrizione}</option>))}
                            </select>
                          ) : metodoLabel(c.modalita_pagamento)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {editId === c.id ? (
                              <>
                                <Button size="sm" variant="ghost" onClick={salvaEdit}>
                                  <Save className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditId(null)}>
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => startEdit(c)}>
                                  <Save className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => elimina(c.id)}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Pannello pagamenti */}
                      {expandedId === c.id && (
                        <TableRow>
                          <TableCell colSpan={NUM_COL} className="bg-muted/20">
                            <div className="p-2 space-y-3">
                              <p className="text-sm font-medium">Pagamenti</p>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-left text-muted-foreground">
                                    <th className="pb-1">Data</th>
                                    <th className="pb-1 text-right">Importo</th>
                                    <th className="pb-1">Modalita'</th>
                                    <th className="pb-1">Riferimento</th>
                                    <th className="pb-1">Ricevuta</th>
                                    <th className="pb-1" />
                                  </tr>
                                </thead>
                                <tbody>
                                  {(pagamenti[c.id] ?? []).map((p) => (
                                    <tr key={p.id} className="border-t">
                                      <td className="py-1">{p.data_pagamento}</td>
                                      <td className="py-1 text-right">{fmt.currency(p.importo)}</td>
                                      <td className="py-1">{metodoLabel(p.modalita_pagamento)}</td>
                                      <td className="py-1">{p.riferimento ?? '-'}</td>
                                      <td className="py-1">
                                        <label className="cursor-pointer text-blue-700 inline-flex items-center gap-1">
                                          <Paperclip className="w-3 h-3" />
                                          <span className="text-xs">
                                            {uploadMsg[p.id] ?? 'Allega'}
                                          </span>
                                          <input
                                            type="file"
                                            className="hidden"
                                            onChange={(e) => {
                                              const f = e.target.files?.[0];
                                              if (f) void allegaRicevuta(c.id, p.id, f);
                                            }}
                                          />
                                        </label>
                                      </td>
                                      <td className="py-1">
                                        <button
                                          className="text-destructive"
                                          onClick={() => eliminaPagamento(c.id, p.id)}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                  {(pagamenti[c.id] ?? []).length === 0 && (
                                    <tr>
                                      <td colSpan={6} className="py-2 text-muted-foreground">
                                        Nessun pagamento registrato
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>

                              {/* Form nuovo pagamento */}
                              <div className="flex flex-wrap items-end gap-2 border-t pt-2">
                                <div>
                                  <label className="text-xs text-muted-foreground block">Data</label>
                                  <Input
                                    type="date" className="h-8"
                                    value={nuovoPag.data_pagamento}
                                    onChange={(e) => setNuovoPag({ ...nuovoPag, data_pagamento: e.target.value })}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground block">Importo</label>
                                  <Input
                                    type="number" step="0.01" className="h-8 w-28 text-right"
                                    value={nuovoPag.importo}
                                    onChange={(e) => setNuovoPag({ ...nuovoPag, importo: e.target.value })}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground block">Modalita'</label>
                                  <select
                                    value={nuovoPag.modalita_pagamento}
                                    onChange={(e) => setNuovoPag({ ...nuovoPag, modalita_pagamento: e.target.value })}
                                    className="rounded border border-border bg-card text-foreground px-2 py-1 text-sm h-8"
                                  >
                                    <option value="">-</option>
                                    {metodi.map((m) => (<option key={m.id} value={m.codice}>{m.descrizione}</option>))}
                                  </select>
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground block">Riferimento</label>
                                  <Input
                                    className="h-8" placeholder="Es. CRO bonifico"
                                    value={nuovoPag.riferimento}
                                    onChange={(e) => setNuovoPag({ ...nuovoPag, riferimento: e.target.value })}
                                  />
                                </div>
                                <Button size="sm" onClick={() => registraPagamento(c.id)}>
                                  Registra pagamento
                                </Button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}

                {/* Footer totale */}
                {costi.length > 0 && (
                  <TableRow className="bg-muted/30 font-semibold">
                    <TableCell colSpan={5} className="text-right">Totale mensile</TableCell>
                    <TableCell className="text-right">{fmt.currency(totaleMensile)}</TableCell>
                    <TableCell colSpan={4} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Card>
      )}
    </div>
  );
};
